import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bot, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { BotFunnelView } from "@/components/settings/BotFunnelView";
import { APP_LOCALE, moneyFormatter } from "@/config/currency";
import { useBotFunnel, useBotRules } from "@/hooks/useBotDetection";
import type { ClicksParams } from "@/hooks/useClicksData";
import { botFunnel, type BotFunnelRow, type BotRule } from "@/lib/bot-funnel";
import {
  addPreset,
  botSelectionOf,
  describeBotSelection,
  loadPresets,
  matchPreset,
  persistPresets,
  presetVerdict,
  removePreset,
  rulesForSelection,
  sameSelection,
  withBotSelection,
  type BotPreset,
  type BotSelection,
} from "@/lib/bot-presets";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import { parseUrlActiveFilters, serializeUrlActiveFilters } from "@/lib/url-active-filters";
import { cn } from "@/lib/utils";

const int = new Intl.NumberFormat(APP_LOCALE);
const euro = moneyFormatter({ maximumFractionDigits: 0 });
const signed = (n: number, f: (v: number) => string) => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${f(Math.abs(n))}`;
const ddmmyyyy = (iso: string) => iso.split("-").reverse().join(".");
const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : String(Date.now());
const keysOn = (rules: BotRule[]) => rules.filter((r) => r.enabled).map((r) => r.key);
const sameKeys = (a: string[], b: string[]) => [...a].sort().join("\u0000") === [...b].sort().join("\u0000");

/** Which Keitaro fields a rule reads (refresh_clicks_daily, 20261019170000 and 20261021160000). */
const READS: Record<string, string> = {
  user_agent: "user_agent contains an entry below",
  empty_browser: "browser is empty",
  old_chrome: "browser is Chrome, browser_version below 110",
  language_unknown: "language is missing or Unknown",
  device_os: "device_type mobile, os Windows / macOS / Linux",
  datacenter_isp: "isp contains an entry below",
  proxy: "is_using_proxy or is_proxy",
  ip_burst: "ip: 20+ clicks that day",
  ip_many_visitors: "ip: 10+ visitor_code that day",
  keitaro_flag: "is_bot",
};

/** Hovering a rule in the funnel: what it matches, and where that is set. */
function RuleInfo({ rule }: { rule: BotRule }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="font-medium">{rule.label}</div>
      <p className="text-xs text-muted-foreground">{rule.description}</p>
      <p className="text-xs">
        <span className="text-muted-foreground">Keitaro: </span>
        <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{READS[rule.key] ?? "—"}</code>
      </p>
      {rule.patterns.length ? (
        <div>
          <div className="text-xs text-muted-foreground">
            {rule.patterns.length} {rule.patterns.length === 1 ? "entry" : "entries"}, case-insensitive “contains”
          </div>
          <div className="mt-1 max-h-40 overflow-y-auto rounded border border-border p-1.5 font-mono text-xs leading-5">
            {rule.patterns.map((p) => (
              <div key={p} className="truncate" title={p}>
                {p}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <p className="border-t border-border pt-2 text-[11px] text-muted-foreground">
        The same in every preset: lists and thresholds are worked into each click by the hourly refresh. Admins
        edit them in Settings → Bot Detection.
      </p>
    </div>
  );
}

interface Choice {
  id: string;
  name: string;
  hint: string;
  selection: BotSelection;
  preset?: BotPreset;
}

/** A preset in the list: what it hides in this report, as a bar and in words. */
function PresetRow({ choice, rows, rules, active, applied, onLoad, onApply, onDelete }: {
  choice: Choice;
  rows: BotFunnelRow[] | undefined;
  rules: BotRule[];
  active: boolean;
  applied: boolean;
  onLoad: () => void;
  onApply: () => void;
  onDelete?: () => void;
}) {
  const f = rows ? botFunnel(rows, rulesForSelection(rules, choice.selection)) : undefined;
  const hidden = f ? f.total.clicks - f.clean.clicks : 0;
  const share = f && f.total.clicks > 0 ? hidden / f.total.clicks : 0;
  const verdict = f ? presetVerdict(f.total, f.clean) : undefined;
  return (
    <div className="flex items-start gap-1">
      <button
        type="button"
        onClick={onLoad}
        onDoubleClick={onApply}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onApply();
          }
        }}
        aria-pressed={active}
        title="Click: look at it in the funnel · double-click or Enter: apply"
        className={cn(
          "grid min-w-0 flex-1 cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded-md border px-2.5 py-2 text-left transition-colors",
          active ? "border-chart-1 bg-chart-1/10" : "border-transparent hover:bg-muted",
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <span className="truncate">{choice.name}</span>
          {applied ? <span className="shrink-0 rounded-full bg-chart-1 px-1.5 text-[10px] font-normal text-background">Applied</span> : null}
        </span>
        <span className="text-right text-xs tabular-nums">
          {!f ? "…" : choice.selection.kind === "all" ? `${int.format(f.total.clicks)} clicks` : `−${(share * 100).toFixed(1)}% clicks`}
        </span>
        <span className="col-span-2 line-clamp-2 text-xs text-muted-foreground" title={choice.hint}>
          {choice.hint}
        </span>
        <span
          className={cn(
            "col-span-2 mt-1 justify-self-start rounded-full bg-muted px-1.5 text-[11px] whitespace-nowrap empty:hidden",
            verdict?.tone === "ok" ? "signal-text-positive" : verdict?.tone === "warn" ? "signal-text-warning" : "text-muted-foreground",
          )}
        >
          {verdict?.text}
        </span>
        <span className="col-span-2 mt-1.5 flex h-1 overflow-hidden rounded-full bg-chart-1" aria-hidden>
          <span className="bot-cut ml-auto h-full transition-[width] duration-[400ms]" style={{ width: `${share * 100}%` }} />
        </span>
      </button>
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          title="Delete this preset"
          aria-label={`Delete preset ${choice.name}`}
          className="mt-2 shrink-0 cursor-pointer rounded p-1 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Clicks → Bots: the "Bot lab" window. Which clicks this report hides as bots -
 * "All traffic" (nothing, the default), "Global rules" (what Settings switches
 * on), or a preset of rules saved per browser like Views - with the Settings
 * funnel over the report's own period and filters. Picking a preset or ticking
 * a rule changes only the window (a draft); Apply writes the is_own_bot filter
 * in the URL, so the grid, the chart, the Bots columns and saved views follow
 * it (src/lib/bot-presets.ts). Closing keeps the draft, marked on the button.
 */
export function BotsControl({ params }: { params: ClicksParams }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = botSelectionOf(parseUrlActiveFilters(searchParams.get("filters")));
  const { data: rules = [] } = useBotRules();
  const ruleLabel = (key: string) => rules.find((r) => r.key === key)?.label ?? key;
  const [presets, setPresets] = useState<BotPreset[]>(() => loadPresets());
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BotSelection | null>(null);
  const [basedOn, setBasedOn] = useState<string | null>(null);
  const [lastToggle, setLastToggle] = useState<{ key: string; seq: number } | null>(null);
  const [name, setName] = useState("");

  const { current: period } = getDateRangeDates(params.dateRange, new Date(), params.customFrom, params.customTo);
  const funnelQuery = useBotFunnel(period.start, period.end, params.activeFilters, open);
  const rows = funnelQuery.data;

  const current = draft ?? applied;
  const dirty = !sameSelection(current, applied);
  const draftRules = rulesForSelection(rules, current);
  const appliedRules = rulesForSelection(rules, applied);
  const draftKeys = keysOn(draftRules);
  const funnel = rows ? botFunnel(rows, draftRules) : undefined;
  const appliedClean = rows ? botFunnel(rows, appliedRules).clean : undefined;
  const base = presets.find((p) => p.id === basedOn);
  const describe = (s: BotSelection) => describeBotSelection(s, presets, ruleLabel);
  const globalOn = rules.filter((r) => r.enabled);

  const choices: Choice[] = [
    { id: "all", name: "All traffic", hint: "Nothing hidden: totals match Keitaro", selection: { kind: "all" } },
    {
      id: "global",
      name: "Global rules",
      hint: globalOn.length ? `${globalOn.map((r) => r.label).join(" + ")} · set in Settings` : "No rule is on in Settings yet",
      selection: { kind: "global" },
    },
    ...presets.map((p) => ({
      id: p.id,
      name: p.name,
      hint: `${p.rules.length} ${p.rules.length === 1 ? "rule" : "rules"}: ${rules
        .filter((r) => p.rules.includes(r.key))
        .map((r) => r.label)
        .join(" + ")}`,
      selection: { kind: "rules", rules: p.rules } as const,
      preset: p,
    })),
  ];

  const reset = () => {
    setDraft(null);
    setBasedOn(null);
    setLastToggle(null);
  };
  const apply = (next: BotSelection) => {
    setSearchParams((prev) => {
      const serialized = serializeUrlActiveFilters(withBotSelection(parseUrlActiveFilters(prev.get("filters")), next));
      if (serialized) prev.set("filters", serialized);
      else prev.delete("filters");
      // Hiding bots changes the row count; page 7 of 2 shows nothing.
      prev.set("page", "1");
      return prev;
    });
    reset();
    setOpen(false);
  };
  const load = (c: Choice) => {
    setDraft(sameSelection(c.selection, applied) ? null : c.selection);
    setBasedOn(c.preset?.id ?? null);
    setLastToggle(null);
  };
  // Ticking back to what is applied is no draft; no rule left is All traffic.
  const toggle = (rule: BotRule, on: boolean) => {
    const next = on ? [...draftKeys, rule.key] : draftKeys.filter((k) => k !== rule.key);
    setDraft(
      sameKeys(next, keysOn(appliedRules)) ? null : next.length ? { kind: "rules", rules: next } : { kind: "all" },
    );
    setLastToggle((t) => ({ key: rule.key, seq: (t?.seq ?? 0) + 1 }));
  };
  const savePresets = (list: BotPreset[]) => setPresets(persistPresets(list));
  // Saving applies too: pick the rules, name them, look at the numbers.
  const saveAsNew = () => {
    const trimmed = name.trim();
    if (!trimmed || !draftKeys.length) return;
    savePresets(addPreset(presets, { id: newId(), name: trimmed, rules: draftKeys }));
    setName("");
    apply({ kind: "rules", rules: draftKeys });
  };
  const update = () => {
    if (!base) return;
    savePresets(presets.map((p) => (p.id === base.id ? { ...p, rules: [...draftKeys].sort() } : p)));
    apply({ kind: "rules", rules: draftKeys });
  };
  const remove = (p: BotPreset) => {
    const before = presets;
    savePresets(removePreset(presets, p.id));
    if (basedOn === p.id) setBasedOn(null);
    toast(`Preset “${p.name}” deleted`, { action: { label: "Undo", onClick: () => savePresets(before) } });
  };

  const overwrites = presets.some((p) => p.name.trim().toLowerCase() === name.trim().toLowerCase());
  const edited = !!base && dirty && !sameKeys(base.rules, draftKeys);
  const delta =
    funnel && appliedClean
      ? {
          clicks: funnel.clean.clicks - appliedClean.clicks,
          sales: funnel.clean.sales - appliedClean.sales,
          revenue: funnel.clean.revenue - appliedClean.revenue,
        }
      : undefined;
  const periodLabel = `${period.start ? ddmmyyyy(period.start) : "All time"} – ${ddmmyyyy(period.end)}`;
  const isActive = (c: Choice) =>
    sameSelection(c.selection, current) || (c.preset !== undefined && current.kind === "rules" && matchPreset(current.rules, [c.preset]) !== undefined);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("relative cursor-pointer gap-2", applied.kind !== "all" && "border-chart-1 bg-chart-1/10 hover:bg-chart-1/15")}
          title="Which clicks this report hides as bots"
        >
          <Bot className="h-4 w-4" />
          {applied.kind === "all" ? "Bots: All traffic" : `Bots hidden: ${describe(applied)}`}
          {dirty ? (
            <span className="absolute -top-1 -right-1 size-2 rounded-full bg-chart-1" title="A draft is waiting in the Bots window" />
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 p-0 sm:max-w-[min(80rem,calc(100vw-2rem))]"
        // Start on the picked preset: the first button's focus ring would read as picked too.
        onOpenAutoFocus={(e) => {
          const picked = (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>('[aria-pressed="true"]');
          if (picked) {
            e.preventDefault();
            picked.focus();
          }
        }}
      >
        <div className="border-b border-border px-6 py-4 pr-12">
          <DialogTitle>Bot lab — bots in this report</DialogTitle>
          <DialogDescription className="mt-1 text-xs">
            {periodLabel} · the report's period and filters; column filters in the table are not counted here. Click
            a preset or tick rules to see the funnel; nothing changes in the report until Apply.
          </DialogDescription>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
            <div className="space-y-1 xl:sticky xl:top-0 xl:self-start" aria-label="Presets">
              {choices.map((c) => (
                <PresetRow
                  key={c.id}
                  choice={c}
                  rows={rows}
                  rules={rules}
                  active={isActive(c)}
                  applied={sameSelection(c.selection, applied)}
                  onLoad={() => load(c)}
                  onApply={() => apply(c.selection)}
                  onDelete={c.preset ? () => remove(c.preset!) : undefined}
                />
              ))}
              <p className="px-2.5 pt-2 text-[11px] text-muted-foreground">
                Presets are kept in this browser, like Views. Double-click one to apply it at once.
              </p>
            </div>

            <div className="min-w-0">
              {funnelQuery.isError ? (
                <p className="signal-text-negative text-sm">Could not load the funnel: {String(funnelQuery.error?.message ?? "")}</p>
              ) : !funnel ? (
                <div className="flex h-64 items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <BotFunnelView
                  funnel={funnel}
                  saved={appliedRules}
                  lastToggle={lastToggle}
                  onToggle={toggle}
                  periodLabel={periodLabel}
                  control="checkbox"
                  ruleInfo={(rule) => <RuleInfo rule={rule} />}
                  draftLabel="changed, not applied yet"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-3">
          <div role="status" aria-live="polite" className="min-w-0 text-sm">
            {dirty ? (
              <>
                <div className="font-medium">
                  <span className="mr-1.5 inline-block size-1.5 rounded-full bg-chart-1 align-middle" />
                  Draft: {describe(current)}
                  {edited ? <span className="font-normal text-muted-foreground"> (edited from “{base.name}”)</span> : null}
                </div>
                {delta ? (
                  <div className="text-xs text-muted-foreground tabular-nums">
                    vs applied: clean {signed(delta.clicks, int.format)} clicks ·{" "}
                    <span className={cn((delta.sales < 0 || delta.revenue < 0) && "signal-text-negative")}>
                      {signed(delta.sales, int.format)} sales · {signed(delta.revenue, euro.format)}
                    </span>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="font-medium">Applied: {describe(applied)}</div>
                <div className="text-xs text-muted-foreground">No changes: pick a preset or tick rules</div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" className="cursor-pointer" disabled={!dirty} onClick={reset}>
              Discard
            </Button>
            <Input
              className="h-9 w-44"
              placeholder="Name this preset"
              aria-label="Preset name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveAsNew();
              }}
            />
            <Button variant="outline" className="cursor-pointer" disabled={!name.trim() || !draftKeys.length} onClick={saveAsNew}>
              {overwrites ? `Overwrite “${name.trim()}”` : "Save as new"}
            </Button>
            {edited ? (
              <Button variant="outline" className="cursor-pointer" onClick={update}>
                Update “{base.name}”
              </Button>
            ) : null}
            <Button className="cursor-pointer" disabled={!dirty} onClick={() => apply(current)}>
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
